import React, { useState, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useThemeTokens } from '../../themes'

export interface DropdownOption {
  id: string | number
  label: string
  value: unknown
  icon?: ReactNode
  disabled?: boolean
  description?: string
  metadata?: Record<string, unknown>
}

interface DropdownProps {
  options: DropdownOption[]
  value?: unknown
  onSelect: (option: DropdownOption) => void
  placeholder?: string
  label?: string
  required?: boolean
  disabled?: boolean
  searchable?: boolean
  renderOption?: (option: DropdownOption, isHighlighted: boolean) => ReactNode
  renderSelected?: (option: DropdownOption) => ReactNode
  className?: string
  error?: string
  filterFunction?: (options: DropdownOption[], searchTerm: string) => DropdownOption[]
}

export const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onSelect,
  placeholder = "Select an option...",
  label,
  required = false,
  disabled = false,
  searchable = false,
  renderOption,
  renderSelected,
  className = '',
  error,
  filterFunction
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredOptions, setFilteredOptions] = useState<DropdownOption[]>(options)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(null)
  
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  
  const theme = useThemeTokens()

  // Find selected option on mount or when value changes
  useEffect(() => {
    const option = options.find(opt => opt.value === value)
    setSelectedOption(option || null)
    if (option && searchable) {
      setSearchTerm(option.label)
    }
  }, [value, options, searchable])

  // Filter options based on search term
  useEffect(() => {
    if (!searchable || !searchTerm) {
      setFilteredOptions(options)
      return
    }

    if (filterFunction) {
      setFilteredOptions(filterFunction(options, searchTerm))
    } else {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (option.description && option.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      setFilteredOptions(filtered)
    }
    setHighlightedIndex(-1)
  }, [options, searchTerm, searchable, filterFunction])

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        if (searchable && selectedOption) {
          setSearchTerm(selectedOption.label)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [searchable, selectedOption])

  const handleToggle = () => {
    if (disabled) return
    const willOpen = !isOpen
    setIsOpen(willOpen)
    if (searchable && willOpen) {
      setTimeout(() => inputRef.current?.focus(), 0)
      ensureDropdownVisible()
    } else if (willOpen) {
      ensureDropdownVisible()
    }
  }

  const handleSelect = (option: DropdownOption) => {
    if (option.disabled) return
    
    setSelectedOption(option)
    setIsOpen(false)
    onSelect(option)
    
    if (searchable) {
      setSearchTerm(option.label)
      inputRef.current?.blur()
    } else {
      buttonRef.current?.blur()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
        return
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => {
          const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : 0
          return filteredOptions[nextIndex]?.disabled ? 
            (nextIndex < filteredOptions.length - 1 ? nextIndex + 1 : 0) : nextIndex
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => {
          const nextIndex = prev > 0 ? prev - 1 : filteredOptions.length - 1
          return filteredOptions[nextIndex]?.disabled ? 
            (nextIndex > 0 ? nextIndex - 1 : filteredOptions.length - 1) : nextIndex
        })
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        if (searchable) {
          inputRef.current?.blur()
        } else {
          buttonRef.current?.blur()
        }
        break
    }
  }

  // Auto-scroll functionality to ensure dropdown is visible
  const ensureDropdownVisible = () => {
    if (!dropdownRef.current) return
    
    // Small delay to ensure dropdown is rendered
    setTimeout(() => {
      if (!dropdownRef.current) return
      
      const rect = dropdownRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - rect.bottom
      const dropdownHeight = 300 // maxHeight of dropdown
      
      // If dropdown would be cut off and there's not enough space below
      if (spaceBelow < dropdownHeight + 50) { // 50px buffer
        const scrollAmount = Math.max(0, (dropdownHeight + 50) - spaceBelow)
        window.scrollBy({
          top: scrollAmount,
          behavior: 'smooth'
        })
      }
    }, 100)
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    if (!isOpen) {
      setIsOpen(true)
      ensureDropdownVisible()
    }
  }

  // Styles
  const containerStyles: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  }

  const labelStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.colors.text,
    fontFamily: theme.typography.fontFamily,
    margin: 0,
  }

  const triggerStyles: React.CSSProperties = {
    width: '100%',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `1px solid ${error ? theme.colors.error : theme.colors.border}`,
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.typography.fontSize.md,
    fontFamily: theme.typography.fontFamily,
    color: selectedOption ? theme.colors.text : theme.colors.textSecondary,
    backgroundColor: theme.colors.surface,
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '48px',
    opacity: disabled ? 0.6 : 1,
  }

  const dropdownStyles: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: theme.spacing.xs,
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.lg,
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
  }

  const optionStyles = (isHighlighted: boolean, isDisabled: boolean): React.CSSProperties => ({
    padding: `${theme.spacing.md} ${theme.spacing.md}`,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    backgroundColor: isHighlighted ? theme.colors.primary + '10' : 'transparent',
    borderBottom: `1px solid ${theme.colors.border}`,
    fontSize: theme.typography.fontSize.md,
    fontFamily: theme.typography.fontFamily,
    color: isDisabled ? theme.colors.textSecondary : theme.colors.text,
    opacity: isDisabled ? 0.5 : 1,
    transition: 'all 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  })

  const errorStyles: React.CSSProperties = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.error,
    margin: 0,
  }

  const ChevronIcon = () => (
    <svg 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2"
      style={{ 
        transition: 'transform 0.2s ease',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
      }}
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )

  const defaultRenderOption = (option: DropdownOption, isHighlighted: boolean) => (
    <div style={optionStyles(isHighlighted, option.disabled || false)}>
      {option.icon && <span>{option.icon}</span>}
      <div style={{ flex: 1 }}>
        <div>{option.label}</div>
        {option.description && (
          <div style={{ 
            fontSize: theme.typography.fontSize.sm, 
            color: theme.colors.textSecondary,
            marginTop: '2px'
          }}>
            {option.description}
          </div>
        )}
      </div>
    </div>
  )

  const defaultRenderSelected = (option: DropdownOption) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
      {option.icon && <span>{option.icon}</span>}
      <span>{option.label}</span>
    </div>
  )

  return (
    <div style={containerStyles} className={className} ref={dropdownRef}>
      {label && (
        <label style={labelStyles}>
          {label}
          {required && <span style={{ color: theme.colors.error }}> *</span>}
        </label>
      )}
      
      {searchable ? (
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            style={triggerStyles}
          />
          <div 
            style={{ 
              position: 'absolute', 
              right: theme.spacing.sm, 
              top: '50%', 
              transform: 'translateY(-50%)',
              pointerEvents: 'none'
            }}
          >
            <ChevronIcon />
          </div>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={triggerStyles}
        >
          <div>
            {selectedOption ? (
              renderSelected ? renderSelected(selectedOption) : defaultRenderSelected(selectedOption)
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronIcon />
        </button>
      )}

      {isOpen && (
        <div style={dropdownStyles}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{ borderBottom: index === filteredOptions.length - 1 ? 'none' : undefined }}
              >
                {renderOption ? 
                  renderOption(option, index === highlightedIndex) : 
                  defaultRenderOption(option, index === highlightedIndex)
                }
              </div>
            ))
          ) : (
            <div style={optionStyles(false, false)}>
              <span style={{ color: theme.colors.textSecondary }}>
                {searchable && searchTerm ? `No results for "${searchTerm}"` : 'No options available'}
              </span>
            </div>
          )}
        </div>
      )}

      {error && (
        <p style={errorStyles}>{error}</p>
      )}
    </div>
  )
} 